import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";

export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const mustChange = !!user?.must_change_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Refresh user from server so must_change_password = false
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Password updated. Welcome aboard!");
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC] p-6" data-testid="change-password-page">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm w-full max-w-md p-7">
        <div className="flex items-center gap-2.5 mb-1">
          <span className="bg-sky-100 text-sky-700 rounded-lg p-2"><ShieldCheck size={20}/></span>
          <h1 className="font-heading text-xl font-semibold text-slate-900">Change your password</h1>
        </div>
        <p className="text-sm text-slate-600 mb-5">
          {mustChange
            ? "For security, please update your default password before continuing."
            : "Update your account password."}
        </p>

        <form onSubmit={handleSubmit} className="space-y-3" data-testid="change-password-form">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Current password</label>
            <input required type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                   data-testid="current-password-input"
                   placeholder={mustChange ? "Solemnity@123" : "Your current password"}
                   className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">New password</label>
            <input required type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                   data-testid="new-password-input" placeholder="At least 6 characters"
                   className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Confirm new password</label>
            <input required type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                   data-testid="confirm-password-input" placeholder="Re-enter new password"
                   className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
          </div>

          {error && <div data-testid="change-password-error" className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

          <button disabled={submitting} data-testid="submit-change-password-btn"
                  className="w-full bg-sky-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-sky-600 disabled:opacity-60 inline-flex items-center justify-center gap-2">
            <KeyRound size={14}/> {submitting ? "Updating…" : "Update password"}
          </button>

          {mustChange && (
            <button type="button" onClick={logout} className="w-full text-xs text-slate-500 hover:text-slate-800 mt-1" data-testid="logout-instead-btn">
              Cancel and sign out
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
